import { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { theme, scaled } from '../styles/theme';
import { DateField } from './DateField';
import { useT } from '../i18n';

// Figma V2 specs (Group 142, layout_UZYM4A):
//   – Header row: UI(S) sans 16 px. Label `#000000` left, date range
//     `#7E7E7E` right. Both regular weight.
//   – Track: 2 px tall. Inactive `#7E7E7E`, active range mint green
//     (`var(--color-data)` via the `.color-data` scope).
//   – Handles: 14 × 14 circles. Mint-green fill, `#D9D9D9` 2 px stroke,
//     no drop shadow.
const Container = styled.div`
  padding: 0;
  /* Grid item in FilterPanel's FilterGrid: without min-width:0 the header
     text would set a min-content floor and let the grid overflow the card. */
  min-width: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* The two date fields drop under the label rather than squeezing it once
     the filter column gets narrow. */
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-bottom: 8px;
`;

const Label = styled.span`
  font-family: var(--sans-stack);
  font-size: ${scaled(16)};
  font-weight: 400;
  color: #000000;
`;

// Typed entry for an exact range, each field with its own compact calendar.
// The slider alone made short windows (a single month) near-impossible to hit
// without zooming the page far in.
const DateFields = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const DateSeparator = styled.span`
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
  color: #7E7E7E;
`;

const SliderContainer = styled.div`
  position: relative;
  height: 24px;
`;

const SliderTrack = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  background: #7E7E7E;
  transform: translateY(-50%);
`;

const SliderRange = styled.div<{ left: number; right: number }>`
  position: absolute;
  top: 50%;
  left: ${p => p.left}%;
  right: ${p => 100 - p.right}%;
  height: 2px;
  background: var(--color-data);
  transform: translateY(-50%);
`;

const RangeInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  pointer-events: none;
  margin: 0;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    height: 14px;
    width: 14px;
    border-radius: 50%;
    background: var(--color-data);
    border: 2px solid #D9D9D9;
    box-shadow: none;
    cursor: pointer;
    pointer-events: all;
  }

  &::-moz-range-thumb {
    height: 14px;
    width: 14px;
    border-radius: 50%;
    background: var(--color-data);
    border: 2px solid #D9D9D9;
    box-shadow: none;
    cursor: pointer;
    pointer-events: all;
  }

  /* The whole input has pointer-events:none and only the thumb re-enables them,
     so the 14px Figma thumb is the entire drag target — fiddly to grab with a
     finger. Enlarge it to 24px on touch (desktop keeps the 14px spec). */
  @media (hover: none), (pointer: coarse) {
    &::-webkit-slider-thumb { height: 24px; width: 24px; }
    &::-moz-range-thumb { height: 24px; width: 24px; }
  }
`;

// Quick date-range shortcuts. Deliberately rendered as plain text links — not
// the shared `tab-buttons` control — so they read as filter shortcuts attached
// to the slider, distinct from the segmented toggles up in the chart header.
const QuickRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  margin-top: 10px;
`;

const QuickLink = styled.button<{ active?: boolean }>`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.xs};
  font-weight: ${p => p.active ? theme.fontWeight.semibold : theme.fontWeight.normal};
  color: ${p => p.active ? 'var(--color-data-700)' : theme.colors.text.muted};
  text-decoration: ${p => p.active ? 'underline' : 'none'};
  text-underline-offset: 3px;
  &:hover { color: var(--color-text-default); }
`;

const DAY = 86_400_000;

interface TimeRangeSliderProps {
  minTimestamp: number;
  maxTimestamp: number;
  fromTimestamp: number;
  toTimestamp: number;
  onChange: (from: number, to: number) => void;
}

export function TimeRangeSlider({ minTimestamp, maxTimestamp, fromTimestamp, toTimestamp, onChange }: TimeRangeSliderProps) {
  const m = useT();
  const [localFrom, setLocalFrom] = useState(fromTimestamp);
  const [localTo, setLocalTo] = useState(toTimestamp);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Sync from props when parent changes externally (clear filters, initial load)
  useEffect(() => {
    setLocalFrom(fromTimestamp);
    setLocalTo(toTimestamp);
  }, [fromTimestamp, toTimestamp]);

  // Cleanup on unmount
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const notify = useCallback((from: number, to: number) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onChange(from, to), 150);
  }, [onChange]);

  const handleFromChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Math.min(Number(e.target.value), localTo);
    setLocalFrom(val);
    notify(val, localTo);
  }, [localTo, notify]);

  const handleToChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Math.max(Number(e.target.value), localFrom);
    setLocalTo(val);
    notify(localFrom, val);
  }, [localFrom, notify]);

  // Date fields. Setting one bound past the other drags the other along
  // instead of refusing the entry, so the range stays valid without the user
  // having to reorder their two edits. (DateField itself only reports dates
  // that are complete and inside the data's range.)
  const handleFromInput = useCallback((ts: number) => {
    const to = Math.max(localTo, ts);
    setLocalFrom(ts);
    setLocalTo(to);
    notify(ts, to);
  }, [localTo, notify]);

  const handleToInput = useCallback((ts: number) => {
    const from = Math.min(localFrom, ts);
    setLocalFrom(from);
    setLocalTo(ts);
    notify(from, ts);
  }, [localFrom, notify]);

  const range = maxTimestamp - minTimestamp || 1;
  const leftPct = ((localFrom - minTimestamp) / range) * 100;
  const rightPct = ((localTo - minTimestamp) / range) * 100;

  // Quick windows end at the latest date; "from" is clamped to the data start.
  const within = (a: number, b: number) => Math.abs(a - b) < 2 * DAY;
  const windowFromYears = (years: number) => {
    const d = new Date(maxTimestamp);
    d.setFullYear(d.getFullYear() - years);
    return Math.max(d.getTime(), minTimestamp);
  };
  const applyWindow = (from: number, to: number) => {
    setLocalFrom(from);
    setLocalTo(to);
    clearTimeout(timerRef.current);
    onChange(from, to);
  };
  const isAll = within(localFrom, minTimestamp) && within(localTo, maxTimestamp);
  const quickWindows = [
    { id: '12m', label: m.timeRange.months12, from: windowFromYears(1) },
    { id: '3y', label: m.timeRange.years3, from: windowFromYears(3) },
  ];

  return (
    <Container>
      <Header>
        <Label>{m.timeRange.label}</Label>
        <DateFields>
          <DateField
            ariaLabel={m.timeRange.from}
            value={localFrom}
            min={minTimestamp}
            max={maxTimestamp}
            onChange={handleFromInput}
          />
          <DateSeparator>–</DateSeparator>
          <DateField
            ariaLabel={m.timeRange.to}
            value={localTo}
            min={minTimestamp}
            max={maxTimestamp}
            onChange={handleToInput}
          />
        </DateFields>
      </Header>
      <SliderContainer>
        <SliderTrack />
        <SliderRange left={leftPct} right={rightPct} />
        <RangeInput
          type="range"
          min={minTimestamp}
          max={maxTimestamp}
          value={localFrom}
          onChange={handleFromChange}
          step={86400000}
        />
        <RangeInput
          type="range"
          min={minTimestamp}
          max={maxTimestamp}
          value={localTo}
          onChange={handleToChange}
          step={86400000}
        />
      </SliderContainer>
      <QuickRow>
        {quickWindows.map(w => (
          <QuickLink
            key={w.id}
            active={!isAll && within(localFrom, w.from) && within(localTo, maxTimestamp)}
            onClick={() => applyWindow(w.from, maxTimestamp)}
          >
            {w.label}
          </QuickLink>
        ))}
        <QuickLink active={isAll} onClick={() => applyWindow(minTimestamp, maxTimestamp)}>
          {m.timeRange.all}
        </QuickLink>
      </QuickRow>
    </Container>
  );
}
